import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Stack,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileDropZone } from "../../../components/UIToolkit/Inputs/FileDropZone";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type { PbjShiftImportResult } from "../../../utils/models";

type Props = {
	open: boolean;
	onClose: () => void;
	departmentId: string;
};

type Scope = "current" | "network";

/** "ukg_ready" → "Ukg Ready", "paycom" → "Paycom". */
const sourceLabel = (source: string) => source.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const ResultRow: FC<{ label: string; value: number | string }> = ({ label, value }) => (
	<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
		<Typography variant="body2" color="text.secondary">
			{label}
		</Typography>
		<Typography variant="body2" sx={{ fontWeight: 600 }}>
			{value}
		</Typography>
	</Box>
);

/**
 * PBJ Audit → "Import shifts": upload an HRIS shifts export and run it through that source's ShiftSyncer
 * (the UI equivalent of sync_paycom_shifts_from_cache.py, generalized to any file-based HRIS). The source
 * is chosen from the department's file-importable shift integrations. Supports a dry run, an optional date
 * window, and either the current department or every same-source dept in its network.
 */
export const ImportShiftsDialog: FC<Props> = ({ open, onClose, departmentId }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const [sources, setSources] = useState<string[] | null>(null); // null = loading
	const [source, setSource] = useState("");
	const [file, setFile] = useState<File | null>(null);
	const [scope, setScope] = useState<Scope>("current");
	const [dryRun, setDryRun] = useState(false);
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [isRunning, setIsRunning] = useState(false);
	const [result, setResult] = useState<PbjShiftImportResult | null>(null);

	// Load the department's file-importable shift sources whenever the dialog opens.
	useEffect(() => {
		if (!open) return;
		let cancelled = false;
		setSources(null);
		backendService
			.getImportShiftSources(departmentId)
			.then((res) => {
				if (cancelled) return;
				setSources(res.data);
				setSource(res.data[0] ?? "");
			})
			.catch(() => {
				if (!cancelled) setSources([]);
			});
		return () => {
			cancelled = true;
		};
	}, [open, departmentId]);

	const resetInputs = useCallback(() => {
		setFile(null);
		setScope("current");
		setDryRun(false);
		setStartDate("");
		setEndDate("");
		setResult(null);
	}, []);

	const handleClose = useCallback(() => {
		if (isRunning) return;
		resetInputs();
		onClose();
	}, [isRunning, resetInputs, onClose]);

	const handleRun = useCallback(async () => {
		if (!file || !source || isRunning) return;
		setIsRunning(true);
		setResult(null);
		try {
			const res = await backendService.importHrisShifts({
				departmentId,
				source,
				file,
				syncAllInNetwork: scope === "network",
				dryRun,
				startDate: startDate || undefined,
				endDate: endDate || undefined,
			});
			setResult(res.data);
			showSnackbar(t(res.data.dryRun ? "pbj_model.import.success_dry_run" : "pbj_model.import.success"), "success");
		} catch {
			showSnackbar(t("pbj_model.import.failed"), "error");
		} finally {
			setIsRunning(false);
		}
	}, [file, source, isRunning, departmentId, scope, dryRun, startDate, endDate, showSnackbar, t]);

	const noSources = sources != null && sources.length === 0;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="pbj-import-shifts-dialog">
			<DialogTitle sx={{ fontWeight: 700 }}>{t("pbj_model.import.title")}</DialogTitle>
			<DialogContent>
				<Stack spacing={2.5} sx={{ pt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						{t("pbj_model.import.description")}
					</Typography>

					{noSources ? (
						<Alert severity="warning">{t("pbj_model.import.no_sources")}</Alert>
					) : (
						<>
							<TextField
								select
								label={t("pbj_model.import.source_label")}
								value={source}
								onChange={(e) => setSource(e.target.value)}
								size="small"
								fullWidth
								disabled={sources == null}
								helperText={sources == null ? t("pbj_model.import.loading_sources") : undefined}
								data-testid="pbj-import-source"
							>
								{(sources ?? []).map((s) => (
									<MenuItem key={s} value={s}>
										{sourceLabel(s)}
									</MenuItem>
								))}
							</TextField>

							<FileDropZone
								accept=".xlsx,.xls,.csv"
								hint={t("pbj_model.import.file_hint")}
								onFiles={(files) => setFile(files[0] ?? null)}
								dataTestId="pbj-import-dropzone"
							/>
							{file && (
								<Typography variant="body2" sx={{ fontWeight: 600 }}>
									{file.name}
								</Typography>
							)}

							<FormControl>
								<FormLabel sx={{ fontSize: 14, fontWeight: 600 }}>{t("pbj_model.import.scope_label")}</FormLabel>
								<RadioGroup value={scope} onChange={(_e, v) => setScope(v as Scope)}>
									<FormControlLabel value="current" control={<Radio size="small" />} label={t("pbj_model.import.scope_current")} />
									<FormControlLabel value="network" control={<Radio size="small" />} label={t("pbj_model.import.scope_network")} />
								</RadioGroup>
							</FormControl>

							<Box sx={{ display: "flex", gap: 2 }}>
								<TextField
									type="date"
									label={t("pbj_model.import.start_date")}
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
									size="small"
									fullWidth
									slotProps={{ inputLabel: { shrink: true } }}
								/>
								<TextField
									type="date"
									label={t("pbj_model.import.end_date")}
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
									size="small"
									fullWidth
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Box>

							<FormControlLabel control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />} label={t("pbj_model.import.dry_run")} />
						</>
					)}

					{result && (
						<Alert severity={result.dryRun ? "info" : "success"} sx={{ "& .MuiAlert-message": { width: "100%" } }}>
							<Stack spacing={0.5}>
								<Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
									{t(result.dryRun ? "pbj_model.import.success_dry_run" : "pbj_model.import.success")}
								</Typography>
								<ResultRow label={t("pbj_model.import.result.inserted")} value={result.inserted} />
								<ResultRow label={t("pbj_model.import.result.deleted")} value={result.deleted} />
								<ResultRow label={t("pbj_model.import.result.ignored")} value={result.ignored} />
								<ResultRow label={t("pbj_model.import.result.departments")} value={result.departmentsSynced} />
								<ResultRow label={t("pbj_model.import.result.punch_events")} value={result.punchEvents} />
								{result.unmappedEventTypes.length > 0 && (
									<ResultRow label={t("pbj_model.import.result.unmapped")} value={result.unmappedEventTypes.join(", ")} />
								)}
							</Stack>
						</Alert>
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose} disabled={isRunning} sx={{ textTransform: "none" }}>
					{t("pbj_model.import.cancel")}
				</Button>
				<Button
					onClick={handleRun}
					variant="contained"
					disabled={!file || !source || isRunning || noSources}
					sx={{ textTransform: "none", fontWeight: 600 }}
					data-testid="pbj-import-run"
				>
					{isRunning ? t("pbj_model.import.running") : t(dryRun ? "pbj_model.import.submit_dry_run" : "pbj_model.import.submit")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
